/**
 * Component: RagArticlesIndex
 * Admin tool to view and manage indexed articles with advanced filters and sorting
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { getIndexedArticles, getArticleChunks } from '../utils/rag-articles-api.js';
import { routedFetch } from '../utils/api-router.js';

// Helper to format dates
const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

// Helper to truncate text
const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

export default function RagArticlesIndex({ world }) {
  const { t, loadPageTranslations } = useTranslation();

  // Load page translations on mount
  useEffect(() => {
    loadPageTranslations('CHATBOT');
    loadPageTranslations('ADMIN');
  }, [loadPageTranslations]);

  // Load entity models from RAG actions
  useEffect(() => {
    const loadEntityModels = async () => {
      setModelsLoading(true);
      try {
        // Get AI language from routing_config in localStorage
        const routingConfigStr = localStorage.getItem('routing_config');
        let aiLanguage = 'FR';
        
        if (routingConfigStr) {
          try {
            const routingConfig = JSON.parse(routingConfigStr);
            aiLanguage = routingConfig.text?.['X-Working-Language'] || 'FR';
          } catch (e) {
            console.warn('[RagArticlesIndex] Failed to parse routing_config:', e);
          }
        }
        
        const responseData = await routedFetch(
          `/api/config/read/article-rag-actions?iaLanguage=${aiLanguage}`
        );

        // Extract valid entity classes from response structure { language: { entityClass: ... } }
        const validEntityClasses = new Set();
        const ragActions = responseData.data || {};
        
        Object.values(ragActions).forEach(languageData => {
          if (languageData && typeof languageData === 'object') {
            Object.keys(languageData).forEach(entityClass => {
              validEntityClasses.add(entityClass);
            });
          }
        });

        // Sort and convert to array
        const sortedModels = Array.from(validEntityClasses).sort();
        setEntityModels(sortedModels);
        
        console.log('[RagArticlesIndex] Entity models loaded:', sortedModels);
      } catch (err) {
        console.error('[RagArticlesIndex] Error loading entity models:', err);
        // Fallback to empty list on error
        setEntityModels([]);
      } finally {
        setModelsLoading(false);
      }
    };

    loadEntityModels();
  }, []);

  // State for articles list
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);

  // State for filters
  const [filters, setFilters] = useState({
    state: null,
    titleSearch: '',
    entityModel: '',
    startDate: '',
    endDate: '',
  });

  // State for sorting
  const [sorting, setSorting] = useState({
    sortBy: 'indexed_at',
    sortOrder: 'DESC',
  });

  // State for pagination
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
  });

  // State for chunks
  const [articleChunks, setArticleChunks] = useState({});
  const [chunksLoading, setChunksLoading] = useState(false);
  const [expandedChunk, setExpandedChunk] = useState(null);

  // State for entity models
  const [entityModels, setEntityModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Load articles when filters, sorting, or pagination changes
  useEffect(() => {
    if (world?.id) {
      loadArticles();
    }
  }, [world?.id, filters, sorting, pagination.offset, pagination.limit]);

  const loadArticles = async () => {
    if (!world?.id) {
      console.log('[RagArticlesIndex] No world ID, skipping load');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const options = {
        limit: pagination.limit,
        offset: pagination.offset,
        state: filters.state || null,
        titleSearch: filters.titleSearch || null,
        entityModel: filters.entityModel || null,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      };

      console.log('[RagArticlesIndex] Loading articles with options:', options);

      const result = await getIndexedArticles(world.id, options);

      console.log('[RagArticlesIndex] Articles loaded successfully:', {
        count: result?.articles?.length,
        total: result?.total,
        hasArticles: !!result?.articles,
      });

      setArticles(result?.articles || []);
      setPagination((prev) => ({
        ...prev,
        total: result?.total || 0,
      }));
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error occurred';
      console.error('[RagArticlesIndex] Error details:', {
        message: errorMessage,
        errorType: err?.constructor?.name,
        stack: err?.stack,
      });
      setError(errorMessage);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadChunks = async (articleId) => {
    if (articleChunks[articleId]) {
      return;
    }

    setChunksLoading(true);

    try {
      const result = await getArticleChunks(articleId);
      setArticleChunks((prev) => ({
        ...prev,
        [articleId]: result.chunks || [],
      }));
    } catch (err) {
      console.error('Error loading chunks:', err);
    } finally {
      setChunksLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
  };

  const handleSortChange = (sortBy) => {
    setSorting((prev) => ({
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === 'DESC' ? 'ASC' : 'DESC',
    }));
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
  };

  const handleArticleExpand = async (articleId) => {
    if (expandedArticle === articleId) {
      setExpandedArticle(null);
    } else {
      setExpandedArticle(articleId);
      await loadChunks(articleId);
    }
  };

  const handlePreviousPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination((prev) => ({
        ...prev,
        offset: prev.offset + prev.limit,
      }));
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('ADMIN', 'RAG_ARTICLES_INDEX', 'RAG Articles Index')}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('ADMIN', 'RAG_ARTICLES_DESCRIPTION', 'View and manage indexed articles for RAG-enhanced chatbot')}
        </p>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <h4 className="font-semibold text-gray-900">
          {t('ADMIN', 'FILTERS', 'Filters')}
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* State Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ADMIN', 'STATE', 'State')}
            </label>
            <select
              value={filters.state || ''}
              onChange={(e) =>
                handleFilterChange('state', e.target.value || null)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('ADMIN', 'RAG_ALL', 'All')}</option>
              <option value="public">
                {t('ADMIN', 'RAG_PUBLIC', 'Public')}
              </option>
              <option value="private">
                {t('ADMIN', 'RAG_PRIVATE', 'Private')}
              </option>
            </select>
          </div>

          {/* Title Search Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ADMIN', 'TITLE_SEARCH', 'Title Search')}
            </label>
            <input
              type="text"
              placeholder={t('ADMIN', 'SEARCH_PLACEHOLDER', 'Search by title...')}
              value={filters.titleSearch}
              onChange={(e) =>
                handleFilterChange('titleSearch', e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Entity Model Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ADMIN', 'ENTITY_MODEL', 'Entity Model')}
            </label>
            <select
              value={filters.entityModel}
              onChange={(e) =>
                handleFilterChange('entityModel', e.target.value)
              }
              disabled={modelsLoading || entityModels.length === 0}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {modelsLoading
                  ? t('ADMIN', 'LOADING', 'Loading...')
                  : t('ADMIN', 'RAG_ALL', 'All')}
              </option>
              {entityModels.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ADMIN', 'START_DATE', 'Start Date')}
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                handleFilterChange('startDate', e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* End Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ADMIN', 'END_DATE', 'End Date')}
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Clear Filters Button */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({
                  state: null,
                  titleSearch: '',
                  entityModel: '',
                  startDate: '',
                  endDate: '',
                });
                setPagination((prev) => ({
                  ...prev,
                  offset: 0,
                }));
              }}
              className="w-full px-3 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md font-medium transition-colors"
            >
              {t('ADMIN', 'CLEAR_FILTERS', 'Clear Filters')}
            </button>
          </div>
        </div>
      </div>

      {/* Sorting Section */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => handleSortChange('title')}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
            sorting.sortBy === 'title'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('ADMIN', 'SORT_TITLE', 'Sort by Title')}
          {sorting.sortBy === 'title' && (
            <span className="ml-2">
              {sorting.sortOrder === 'ASC' ? '↑' : '↓'}
            </span>
          )}
        </button>

        <button
          onClick={() => handleSortChange('indexed_at')}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
            sorting.sortBy === 'indexed_at'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('ADMIN', 'SORT_DATE', 'Sort by Date')}
          {sorting.sortBy === 'indexed_at' && (
            <span className="ml-2">
              {sorting.sortOrder === 'ASC' ? '↑' : '↓'}
            </span>
          )}
        </button>

        <button
          onClick={() => handleSortChange('entity_model')}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
            sorting.sortBy === 'entity_model'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('ADMIN', 'SORT_MODEL', 'Sort by Model')}
          {sorting.sortBy === 'entity_model' && (
            <span className="ml-2">
              {sorting.sortOrder === 'ASC' ? '↑' : '↓'}
            </span>
          )}
        </button>

        <button
          onClick={() => handleSortChange('chunks_count')}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
            sorting.sortBy === 'chunks_count'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('ADMIN', 'SORT_CHUNKS', 'Sort by Chunks')}
          {sorting.sortBy === 'chunks_count' && (
            <span className="ml-2">
              {sorting.sortOrder === 'ASC' ? '↑' : '↓'}
            </span>
          )}
        </button>
      </div>

      {/* Stats */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-blue-900">
            {t('ADMIN', 'RAG_STATS', 'Affichage de {{current}} sur {{total}} articles', {
              current: Math.min(
                pagination.offset + pagination.limit,
                pagination.total
              ),
              total: pagination.total,
            })}
          </p>
          <p className="text-xs text-blue-700">
            {t('ADMIN', 'TOTAL_INDEXED', 'Total indexed: {{total}}', {
              total: pagination.total,
            })}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-900">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600">
            {t('ADMIN', 'LOADING', 'Loading...')}
          </p>
        </div>
      )}

      {/* Articles List */}
      {!loading && articles.length > 0 && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto border border-gray-200 rounded-lg">
          {articles.map((article) => (
            <div
              key={article.article_id}
              className="border-b border-gray-100 last:border-b-0"
            >
              {/* Article Summary Row */}
              <button
                onClick={() => handleArticleExpand(article.article_id)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">
                      {article.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {article.slug}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                    {/* State Badge */}
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                        article.state === 'public'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {article.state}
                    </span>

                    {/* Model Badge */}
                    <span className="px-2 py-1 rounded bg-purple-100 text-purple-800 font-medium whitespace-nowrap">
                      {article.entity_model || 'Article'}
                    </span>

                    {/* Chunks Count */}
                    <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 font-medium whitespace-nowrap">
                      {article.chunks_count || 0} chunks
                    </span>

                    {/* Expand Icon */}
                    <span className="text-gray-400">
                      {expandedArticle === article.article_id ? '▼' : '▶'}
                    </span>
                  </div>
                </div>
              </button>

              {/* Article Details */}
              {expandedArticle === article.article_id && (
                <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 space-y-4 text-xs">
                  {/* Metadata Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {t('ADMIN', 'RAG_INDEXED', 'Indexed At')}:
                      </p>
                      <p className="text-gray-600">
                        {formatDate(article.indexed_at)}
                      </p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-900">
                        {t('ADMIN', 'RAG_UPDATED', 'Updated At')}:
                      </p>
                      <p className="text-gray-600">
                        {formatDate(article.updated_at)}
                      </p>
                    </div>
                  </div>

                  {/* Context */}
                  {article.context && (
                    <div>
                      <p className="font-semibold text-gray-900">
                        {t('ADMIN', 'RAG_CONTEXT', 'Context')}:
                      </p>
                      <p className="text-gray-600 line-clamp-3">
                        {article.context}
                      </p>
                    </div>
                  )}

                  {/* Chunks List */}
                  {articleChunks[article.article_id] &&
                    articleChunks[article.article_id].length > 0 && (
                      <div>
                        <p className="font-semibold text-gray-900 mb-2">
                          {t('ADMIN', 'CHUNKS', 'Chunks')} (
                          {articleChunks[article.article_id].length}):
                        </p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {articleChunks[article.article_id].map((chunk) => (
                            <div
                              key={`${article.article_id}-${chunk.chunk_order}`}
                              className="bg-white border border-gray-200 rounded p-2 cursor-pointer hover:bg-gray-100"
                              onClick={() =>
                                setExpandedChunk(
                                  expandedChunk ===
                                    `${article.article_id}-${chunk.chunk_order}`
                                    ? null
                                    : `${article.article_id}-${chunk.chunk_order}`
                                )
                              }
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900">
                                    {chunk.chunk_title || `Chunk #${chunk.chunk_order}`}
                                  </p>
                                  <p className="text-gray-600 truncate">
                                    {truncateText(
                                      chunk.chunk_content,
                                      80
                                    )}
                                  </p>
                                </div>
                                <span className="text-gray-500 whitespace-nowrap">
                                  {chunk.chunk_content?.length || 0} chars
                                </span>
                              </div>

                              {/* Chunk Details */}
                              {expandedChunk ===
                                `${article.article_id}-${chunk.chunk_order}` && (
                                <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                  {chunk.chunk_context && (
                                    <div>
                                      <p className="font-semibold text-gray-900">
                                        {t('ADMIN', 'CONTEXT', 'Context')}:
                                      </p>
                                      <pre className="text-gray-600 bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                                        {chunk.chunk_context}
                                      </pre>
                                    </div>
                                  )}

                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {t('ADMIN', 'CONTENT', 'Content')}:
                                    </p>
                                    <p className="text-gray-600 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                                      {chunk.chunk_content}
                                    </p>
                                  </div>

                                  {chunk.metadata &&
                                    Object.keys(chunk.metadata).length > 0 && (
                                      <div>
                                        <p className="font-semibold text-gray-900">
                                          {t('ADMIN', 'METADATA', 'Metadata')}:
                                        </p>
                                        <pre className="text-gray-600 bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                                          {JSON.stringify(
                                            chunk.metadata,
                                            null,
                                            2
                                          )}
                                        </pre>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* View on WorldAnvil Link */}
                  <a
                    href={article.worldanvil_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {t('ADMIN', 'RAG_VIEW_WA', 'View on WorldAnvil')} →
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && articles.length === 0 && !error && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600">
            {t('ADMIN', 'RAG_NO_ARTICLES', 'No articles indexed yet')}
          </p>
        </div>
      )}

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={handlePreviousPage}
            disabled={pagination.offset === 0}
            className="px-3 py-1 rounded text-sm bg-gray-200 hover:bg-gray-300 disabled:opacity-50 font-medium"
          >
            {t('ADMIN', 'PREVIOUS', 'Previous')}
          </button>

          <span className="text-sm text-gray-600">
            {t('ADMIN', 'PAGE', 'Page {{current}} of {{total}}', {
              current: currentPage,
              total: totalPages,
            })}
          </span>

          <button
            onClick={handleNextPage}
            disabled={
              pagination.offset + pagination.limit >= pagination.total
            }
            className="px-3 py-1 rounded text-sm bg-gray-200 hover:bg-gray-300 disabled:opacity-50 font-medium"
          >
            {t('ADMIN', 'NEXT', 'Next')}
          </button>
        </div>
      )}
    </div>
  );
}
